/**
 * Internal DNC Management Page
 *
 * Lets a super admin manage the platform-wide Internal Do-Not-Contact list:
 * a master enforcement toggle plus suppression rules (area codes, exact E.164
 * numbers, E.164 prefixes). When enforcement is on, the SMS worker skips the
 * provider call for any recipient that matches a rule; PRE evaluation still
 * runs and is logged so the audit trail is intact — only the message
 * transport is blocked.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import { Card, CardContent, Badge, Button, Switch, cn } from '@respectly/ui';
import {
  ShieldBan,
  ArrowLeft,
  Loader2,
  X,
  Plus,
  Hash,
  Phone,
  Globe2,
  Save,
  ShieldOff,
  Sparkles,
  Activity,
  Clock,
  ChevronRight,
  AlertTriangle,
  FileSearch,
  ListFilter,
} from 'lucide-react';
import {
  useInternalDncStore,
  type InternalDncRules,
  type RecentSuppressedSend,
} from '../../stores/useInternalDncStore';
import { DecisionBadge } from '../../components/policy/DecisionBadge';
import { summarizeDecision } from '../../components/policy/decisionSummary';
import { AddDncRuleModal, type StagedAddition } from './AddDncRuleModal';
import { formatPrefixForDisplay, type RuleKind } from './dncRuleParser';

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

// ============================================
// Helpers
// ============================================

function formatDateTime(value: string | Date | null): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatRelativeTime(value: string | Date | null): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// ============================================
// Page
// ============================================

export function InternalDnc() {
  const config = useInternalDncStore((s) => s.config);
  const isLoadingConfig = useInternalDncStore((s) => s.isLoadingConfig);
  const isSavingConfig = useInternalDncStore((s) => s.isSavingConfig);
  const configError = useInternalDncStore((s) => s.configError);
  const loadConfig = useInternalDncStore((s) => s.loadConfig);
  const saveConfig = useInternalDncStore((s) => s.saveConfig);

  const recentSends = useInternalDncStore((s) => s.recentSends);
  const isLoadingRecentSends = useInternalDncStore((s) => s.isLoadingRecentSends);
  const loadRecentSends = useInternalDncStore((s) => s.loadRecentSends);

  // Local draft state — only persisted on Save
  const [draftEnabled, setDraftEnabled] = useState(false);
  const [draftRules, setDraftRules] = useState<InternalDncRules>({
    areaCodes: [],
    exactNumbers: [],
    prefixes: [],
  });
  // Transient provenance map — `${kind}:${value}` -> "from CSV: foo.csv" etc.
  // Reset to {} whenever config syncs from server; only entries added in-session
  // show a source subline.
  const [sources, setSources] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<'all' | RuleKind>('all');
  const [isAddOpen, setIsAddOpen] = useState(false);

  // Sync draft from config when it loads
  useEffect(() => {
    if (config) {
      setDraftEnabled(config.enabled);
      setDraftRules({
        areaCodes: [...(config.rules.areaCodes ?? [])],
        exactNumbers: [...(config.rules.exactNumbers ?? [])],
        prefixes: [...(config.rules.prefixes ?? [])],
      });
      setSources({});
    }
  }, [config]);

  useEffect(() => {
    loadConfig();
    loadRecentSends();
  }, [loadConfig, loadRecentSends]);

  const isDirty = useMemo(() => {
    if (!config) return false;
    return (
      draftEnabled !== config.enabled ||
      !arraysEqual(draftRules.areaCodes, config.rules.areaCodes ?? []) ||
      !arraysEqual(draftRules.exactNumbers, config.rules.exactNumbers ?? []) ||
      !arraysEqual(draftRules.prefixes, config.rules.prefixes ?? [])
    );
  }, [config, draftEnabled, draftRules]);

  const totalRules =
    draftRules.areaCodes.length + draftRules.exactNumbers.length + draftRules.prefixes.length;

  const handleSave = useCallback(async () => {
    const ok = await saveConfig({ enabled: draftEnabled, rules: draftRules });
    if (ok) {
      setSources({});
      loadRecentSends();
    }
  }, [saveConfig, loadRecentSends, draftEnabled, draftRules]);

  const handleReset = useCallback(() => {
    if (config) {
      setDraftEnabled(config.enabled);
      setDraftRules({
        areaCodes: [...(config.rules.areaCodes ?? [])],
        exactNumbers: [...(config.rules.exactNumbers ?? [])],
        prefixes: [...(config.rules.prefixes ?? [])],
      });
      setSources({});
    }
  }, [config]);

  const handleAddRules = useCallback((additions: StagedAddition[]) => {
    if (additions.length === 0) return;
    setDraftRules((prev) => {
      const next: InternalDncRules = {
        areaCodes: [...prev.areaCodes],
        exactNumbers: [...prev.exactNumbers],
        prefixes: [...prev.prefixes],
      };
      for (const a of additions) {
        if (a.kind === 'areaCode' && !next.areaCodes.includes(a.value)) {
          next.areaCodes.push(a.value);
        } else if (a.kind === 'exactNumber' && !next.exactNumbers.includes(a.value)) {
          next.exactNumbers.push(a.value);
        } else if (a.kind === 'prefix' && !next.prefixes.includes(a.value)) {
          next.prefixes.push(a.value);
        }
      }
      return next;
    });
    setSources((prev) => {
      const next = { ...prev };
      for (const a of additions) {
        if (!a.source?.label) continue;
        const sourceLabel =
          a.source.type === 'contact'
            ? `from Contact: ${a.source.label}`
            : a.source.type === 'csv'
            ? `from CSV: ${a.source.label}`
            : a.source.label;
        next[`${a.kind}:${a.value}`] = sourceLabel;
      }
      return next;
    });
  }, []);

  const handleRemoveRule = useCallback((kind: RuleKind, value: string) => {
    setDraftRules((prev) => ({
      areaCodes: kind === 'areaCode' ? prev.areaCodes.filter((v) => v !== value) : prev.areaCodes,
      exactNumbers:
        kind === 'exactNumber' ? prev.exactNumbers.filter((v) => v !== value) : prev.exactNumbers,
      prefixes: kind === 'prefix' ? prev.prefixes.filter((v) => v !== value) : prev.prefixes,
    }));
    setSources((prev) => {
      const next = { ...prev };
      delete next[`${kind}:${value}`];
      return next;
    });
  }, []);

  // Flatten the 3 rule arrays into a single sortable list with type info
  const allRules = useMemo(() => {
    const list: Array<{ kind: RuleKind; value: string; displayValue: string }> = [];
    for (const v of draftRules.exactNumbers) {
      list.push({ kind: 'exactNumber', value: v, displayValue: v });
    }
    for (const v of draftRules.prefixes) {
      list.push({ kind: 'prefix', value: v, displayValue: formatPrefixForDisplay(v) });
    }
    for (const v of draftRules.areaCodes) {
      list.push({ kind: 'areaCode', value: v, displayValue: v });
    }
    return list;
  }, [draftRules]);

  const filteredRules = useMemo(() => {
    if (filter === 'all') return allRules;
    return allRules.filter((r) => r.kind === filter);
  }, [allRules, filter]);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-center gap-4">
          <Link
            to="/settings/policy/dnc"
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </Link>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-100 to-rose-200 dark:from-rose-900/40 dark:to-rose-800/40 flex items-center justify-center">
            <ShieldBan className="w-6 h-6 text-rose-600 dark:text-rose-400" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Internal DNC
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              Block outbound messages to numbers on the platform's internal Do-Not-Contact list
            </p>
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoadingConfig && !config ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6 max-w-5xl mx-auto"
          >
            {/* Status Banner */}
            <StatusBanner
              enabled={draftEnabled}
              onToggle={setDraftEnabled}
              totalRules={totalRules}
              updatedAt={config?.updatedAt ?? null}
              updatedByName={config?.updatedByName ?? null}
            />

            {configError && (
              <motion.div variants={itemVariants}>
                <Card className="border-red-200 dark:border-red-900/40 bg-red-50/60 dark:bg-red-950/20">
                  <CardContent className="p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 dark:text-red-300">{configError}</p>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Suppression Rules */}
            <motion.div variants={itemVariants}>
              <Card className="border-slate-200/60 dark:border-slate-700/60 overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200 dark:border-slate-700">
                    <div>
                      <h2 className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-rose-500" />
                        Suppression Rules
                      </h2>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
                        Add an exact number, a wildcard pattern (e.g.{' '}
                        <code className="font-mono text-amber-600 dark:text-amber-400">+1404555*</code>),
                        or a 3-digit US area code. PRE still runs and logs each decision — only the SMS
                        provider call is blocked.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setIsAddOpen(true)}
                      className="bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white border-0 flex-shrink-0"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1.5" />
                      Add Rule
                    </Button>
                  </div>

                  {/* Filter chips */}
                  {allRules.length > 0 && (
                    <div className="flex items-center gap-1.5 px-5 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/40">
                      <ListFilter className="w-3.5 h-3.5 text-slate-400 mr-0.5" />
                      <FilterChip
                        active={filter === 'all'}
                        onClick={() => setFilter('all')}
                        label="All"
                        count={allRules.length}
                      />
                      <FilterChip
                        active={filter === 'exactNumber'}
                        onClick={() => setFilter('exactNumber')}
                        label="Exact"
                        count={draftRules.exactNumbers.length}
                      />
                      <FilterChip
                        active={filter === 'prefix'}
                        onClick={() => setFilter('prefix')}
                        label="Wildcard"
                        count={draftRules.prefixes.length}
                      />
                      <FilterChip
                        active={filter === 'areaCode'}
                        onClick={() => setFilter('areaCode')}
                        label="Area Code"
                        count={draftRules.areaCodes.length}
                      />
                    </div>
                  )}

                  {/* Rule list */}
                  <div className="min-h-[180px] max-h-[480px] overflow-auto">
                    {allRules.length === 0 ? (
                      <div className="px-5 py-14 text-center">
                        <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
                          <ShieldBan className="w-6 h-6 text-slate-400" />
                        </div>
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
                          No rules yet
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
                          Click <span className="font-semibold">Add Rule</span> to type a number, pick from contacts, or import a CSV.
                        </p>
                      </div>
                    ) : filteredRules.length === 0 ? (
                      <div className="px-5 py-14 text-center text-xs text-slate-500 dark:text-slate-400">
                        No rules of this type.
                      </div>
                    ) : (
                      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                        <AnimatePresence initial={false}>
                          {filteredRules.map((r) => (
                            <motion.li
                              key={`${r.kind}:${r.value}`}
                              layout
                              initial={{ opacity: 0, x: -8 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 8 }}
                              transition={{ duration: 0.15 }}
                            >
                              <RuleRow
                                kind={r.kind}
                                displayValue={r.displayValue}
                                source={sources[`${r.kind}:${r.value}`]}
                                onRemove={() => handleRemoveRule(r.kind, r.value)}
                              />
                            </motion.li>
                          ))}
                        </AnimatePresence>
                      </ul>
                    )}
                  </div>

                  {/* Action footer */}
                  <div className="flex items-center justify-between gap-3 px-5 py-3 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-200 dark:border-slate-700">
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {isDirty ? (
                        <span className="text-amber-600 dark:text-amber-400 font-medium">
                          Unsaved changes
                        </span>
                      ) : (
                        <span>All changes saved</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleReset}
                        disabled={!isDirty || isSavingConfig}
                      >
                        Discard
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={!isDirty || isSavingConfig}
                        className="bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white border-0"
                      >
                        {isSavingConfig ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            Saving…
                          </>
                        ) : (
                          <>
                            <Save className="w-3.5 h-3.5 mr-1.5" />
                            Save Changes
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <AddDncRuleModal
              open={isAddOpen}
              onOpenChange={setIsAddOpen}
              existing={draftRules}
              onAdd={handleAddRules}
            />

            {/* Recently Blocked */}
            <motion.div variants={itemVariants}>
              <Card className="border-slate-200/60 dark:border-slate-700/60">
                <CardContent className="p-0">
                  <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700">
                    <div>
                      <h2 className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                        <Activity className="w-4 h-4 text-rose-500" />
                        Recently Blocked
                        {recentSends.length > 0 && (
                          <Badge variant="secondary" className="text-[10px] tabular-nums ml-1">
                            {recentSends.length}
                          </Badge>
                        )}
                      </h2>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        Recipients whose SMS was suppressed by an active Internal DNC rule
                      </p>
                    </div>
                    <Link
                      to="/settings/policy/decisions"
                      className="text-xs text-rose-600 dark:text-rose-400 hover:underline flex items-center gap-1"
                    >
                      View all decisions
                      <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>

                  {isLoadingRecentSends ? (
                    <div className="flex items-center justify-center h-32">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                  ) : recentSends.length === 0 ? (
                    <div className="px-5 py-12 text-center">
                      <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
                        <ShieldBan className="w-6 h-6 text-slate-400" />
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
                        Nothing blocked yet
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
                        Enable enforcement, add a rule (e.g. <code className="font-mono text-rose-600 dark:text-rose-400">555</code> area code),
                        then send a campaign to a matching number to see it appear here.
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                      {recentSends.map((entry) => (
                        <RecentSendRow key={entry.recipient.id} entry={entry} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

// ============================================
// Status Banner — gradient hero
// ============================================

interface StatusBannerProps {
  enabled: boolean;
  onToggle: (next: boolean) => void;
  totalRules: number;
  updatedAt: string | null;
  updatedByName: string | null;
}

function StatusBanner({ enabled, onToggle, totalRules, updatedAt, updatedByName }: StatusBannerProps) {
  return (
    <motion.div variants={itemVariants}>
      <Card
        className={cn(
          'overflow-hidden border-0 transition-all duration-500',
          enabled
            ? 'bg-gradient-to-br from-rose-500 via-rose-600 to-pink-600 text-white shadow-xl shadow-rose-500/20'
            : 'bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 text-slate-700 dark:text-slate-300',
        )}
      >
        <CardContent className="p-6 relative">
          {/* Decorative blobs */}
          {enabled && (
            <>
              <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-white/10 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-8 left-1/3 w-32 h-32 rounded-full bg-white/5 blur-2xl pointer-events-none" />
            </>
          )}

          <div className="relative flex items-start justify-between gap-6 flex-wrap">
            <div className="flex items-start gap-4 min-w-0">
              <div
                className={cn(
                  'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0',
                  enabled ? 'bg-white/15 backdrop-blur-sm' : 'bg-slate-200 dark:bg-slate-700',
                )}
              >
                {enabled ? (
                  <ShieldBan className="w-6 h-6 text-white" />
                ) : (
                  <ShieldOff className="w-6 h-6 text-slate-500 dark:text-slate-400" />
                )}
              </div>

              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-bold">
                    {enabled ? 'Internal DNC Active' : 'Internal DNC Disabled'}
                  </h2>
                  <Badge
                    className={cn(
                      'text-[10px] uppercase tracking-wide font-semibold border-0',
                      enabled
                        ? 'bg-white/20 text-white'
                        : 'bg-slate-300/70 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
                    )}
                  >
                    {enabled ? 'Live' : 'Off'}
                  </Badge>
                </div>
                <p
                  className={cn(
                    'text-sm mt-1 max-w-xl',
                    enabled ? 'text-white/90' : 'text-slate-500 dark:text-slate-400',
                  )}
                >
                  {enabled
                    ? `Outbound SMS to numbers matching any of the ${totalRules} rule${totalRules === 1 ? '' : 's'} below will be intercepted. PRE still runs and logs every decision.`
                    : 'Outbound SMS will be sent normally. Toggle on to start enforcing the Internal DNC list.'}
                </p>
                {updatedAt && (
                  <p
                    className={cn(
                      'text-xs mt-3 flex items-center gap-1.5',
                      enabled ? 'text-white/70' : 'text-slate-400 dark:text-slate-500',
                    )}
                  >
                    <Clock className="w-3 h-3" />
                    Updated {formatRelativeTime(updatedAt)}
                    {updatedByName && (
                      <>
                        <span className="opacity-50">•</span>
                        <span>by {updatedByName}</span>
                      </>
                    )}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <Switch
                checked={enabled}
                onCheckedChange={onToggle}
                className={cn(
                  'scale-125',
                  enabled && 'data-[state=checked]:bg-white/30',
                )}
              />
              <span
                className={cn(
                  'text-[10px] uppercase tracking-wider font-semibold',
                  enabled ? 'text-white/80' : 'text-slate-400 dark:text-slate-500',
                )}
              >
                Enforcement
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ============================================
// Filter Chip — pill toggle above the unified list
// ============================================

interface FilterChipProps {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
}

function FilterChip({ active, onClick, label, count }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors',
        active
          ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
          : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-rose-300',
      )}
    >
      {label}
      <span className="tabular-nums opacity-70">{count}</span>
    </button>
  );
}

// ============================================
// Rule Row — single suppression rule entry in the unified list
// ============================================

const KIND_ROW_META: Record<RuleKind, { label: string; icon: typeof Hash; iconColor: string; pill: string }> = {
  exactNumber: {
    label: 'Exact',
    icon: Phone,
    iconColor: 'text-emerald-500',
    pill: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  },
  prefix: {
    label: 'Wildcard',
    icon: Globe2,
    iconColor: 'text-amber-500',
    pill: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  },
  areaCode: {
    label: 'Area Code',
    icon: Hash,
    iconColor: 'text-blue-500',
    pill: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  },
};

interface RuleRowProps {
  kind: RuleKind;
  displayValue: string;
  source?: string;
  onRemove: () => void;
}

function RuleRow({ kind, displayValue, source, onRemove }: RuleRowProps) {
  const meta = KIND_ROW_META[kind];
  const Icon = meta.icon;
  return (
    <div className="group flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors">
      <div className={cn('w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0', meta.pill)}>
        <Icon className={cn('w-3.5 h-3.5', meta.iconColor)} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm text-slate-900 dark:text-white truncate">
            {displayValue}
          </span>
          <span className={cn('text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded', meta.pill)}>
            {meta.label}
          </span>
        </div>
        {source && (
          <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5 truncate">{source}</p>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-900/30"
        aria-label={`Remove ${displayValue}`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ============================================
// Recent Send Row
// ============================================

function RecentSendRow({ entry }: { entry: RecentSuppressedSend }) {
  const [open, setOpen] = useState(false);
  const { recipient, decisionLog } = entry;
  const summary = decisionLog ? summarizeDecision(decisionLog) : null;
  const contactName =
    recipient.contact?.firstName || recipient.contact?.lastName
      ? `${recipient.contact?.firstName ?? ''} ${recipient.contact?.lastName ?? ''}`.trim()
      : 'Unknown';

  return (
    <div className="px-5 py-3 hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition-colors">
      <button
        type="button"
        onClick={() => decisionLog && setOpen((v) => !v)}
        className={cn(
          'w-full flex items-start gap-3 text-left',
          decisionLog ? 'cursor-pointer' : 'cursor-default',
        )}
        disabled={!decisionLog}
      >
        {/* Internal DNC pill */}
        <div className="flex-shrink-0 mt-0.5">
          <Badge className="bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border-0 text-[10px]">
            <ShieldBan className="w-3 h-3 mr-1" />
            Blocked
          </Badge>
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-slate-900 dark:text-white truncate">
              {contactName}
            </span>
            <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
              {recipient.contact?.phone ?? '—'}
            </span>
            {decisionLog && <DecisionBadge decision={decisionLog.decision} />}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {recipient.campaign?.name && (
              <>
                <span className="truncate max-w-[200px]">{recipient.campaign.name}</span>
                <span className="opacity-50">•</span>
              </>
            )}
            {recipient.internalDncReason && (
              <>
                <span className="text-rose-600 dark:text-rose-400">
                  {recipient.internalDncReason.replace(/^Suppressed:\s*/, '')}
                </span>
                <span className="opacity-50">•</span>
              </>
            )}
            <span>{formatDateTime(recipient.sentAt)}</span>
          </div>
        </div>

        {decisionLog && (
          <ChevronRight
            className={cn(
              'w-4 h-4 text-slate-400 flex-shrink-0 mt-1 transition-transform',
              open && 'rotate-90',
            )}
          />
        )}
      </button>

      <AnimatePresence>
        {open && decisionLog && summary && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-3 ml-8 space-y-3">
              {/* Plain-English banner */}
              <div
                className={cn(
                  'rounded-lg border p-3',
                  summary.tone === 'success' &&
                    'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-900/40',
                  summary.tone === 'warning' &&
                    'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-900/40',
                  summary.tone === 'error' &&
                    'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-900/40',
                )}
              >
                <p
                  className={cn(
                    'text-xs font-semibold uppercase tracking-wide mb-1',
                    summary.tone === 'success' && 'text-emerald-700 dark:text-emerald-400',
                    summary.tone === 'warning' && 'text-amber-700 dark:text-amber-400',
                    summary.tone === 'error' && 'text-red-700 dark:text-red-400',
                  )}
                >
                  PRE Verdict — {summary.verdict}
                </p>
                <p className="text-sm text-slate-700 dark:text-slate-300">{summary.detail}</p>
              </div>

              {/* Rule trace */}
              {decisionLog.output.trace.length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">
                    Rule Trace
                  </p>
                  <div className="space-y-1">
                    {decisionLog.output.trace.map((t, i) => (
                      <div
                        key={`${t.ruleId}-${i}`}
                        className="flex items-center gap-2 text-xs"
                      >
                        <Badge
                          variant={
                            t.result === 'PASS'
                              ? 'success'
                              : t.result === 'FAIL'
                              ? 'error'
                              : 'secondary'
                          }
                          className="text-[10px] tabular-nums"
                        >
                          {t.result}
                        </Badge>
                        <span className="text-slate-700 dark:text-slate-300">{t.ruleName}</span>
                        <span className="font-mono text-[10px] text-slate-400">
                          {t.ruleId}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Link to full decision */}
              <Link
                to="/settings/policy/decisions"
                className="text-xs text-rose-600 dark:text-rose-400 hover:underline inline-flex items-center gap-1"
              >
                <FileSearch className="w-3 h-3" />
                Open in full decision viewer
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
